import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

const FiltroData = ({ value, loading, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const presets = [
    {
      id: 'diario',
      label: 'Diário',
      getDates: () => {
        const today = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        return { start: toISO(today), end: toISO(today) };
      }
    },
    {
      id: 'esta_semana',
      label: 'Esta Semana',
      getDates: () => {
        const today = new Date();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Segunda-feira
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6); // Domingo
        const pad = (n) => String(n).padStart(2, '0');
        const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        return { start: toISO(startOfWeek), end: toISO(endOfWeek) };
      }
    },
    {
      id: 'mes_atual',
      label: 'Mês Atual',
      getDates: () => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const pad = (n) => String(n).padStart(2, '0');
        const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        return { start: toISO(startOfMonth), end: toISO(endOfMonth) };
      }
    },
    {
      id: 'mes_anterior',
      label: 'Mês Anterior',
      getDates: () => {
        const today = new Date();
        const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        const pad = (n) => String(n).padStart(2, '0');
        const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        return { start: toISO(startOfLastMonth), end: toISO(endOfLastMonth) };
      }
    },
    {
      id: 'trimestre_atual',
      label: 'Trimestre Atual',
      getDates: () => {
        const today = new Date();
        const currentMonth = today.getMonth();
        const quarterStart = Math.floor(currentMonth / 3) * 3;
        const startOfQuarter = new Date(today.getFullYear(), quarterStart, 1);
        const endOfQuarter = new Date(today.getFullYear(), quarterStart + 3, 0);
        const pad = (n) => String(n).padStart(2, '0');
        const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        return { start: toISO(startOfQuarter), end: toISO(endOfQuarter) };
      }
    },
    {
      id: 'ano_atual',
      label: 'Ano Atual',
      getDates: () => {
        const today = new Date();
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        const endOfYear = new Date(today.getFullYear(), 11, 31);
        const pad = (n) => String(n).padStart(2, '0');
        const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        return { start: toISO(startOfYear), end: toISO(endOfYear) };
      }
    },
    {
      id: 'ultimos_6_meses',
      label: 'Últimos 6 Meses',
      getDates: () => {
        const today = new Date();
        const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 6, 1);
        const pad = (n) => String(n).padStart(2, '0');
        const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        return { start: toISO(sixMonthsAgo), end: toISO(today) };
      }
    },
    {
      id: 'personalizado',
      label: 'Data Personalizada',
      getDates: () => value
    }
  ];

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const parseISODateLocal = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const formatDate = (dateStr) => {
    const date = parseISODateLocal(dateStr);
    return date ? date.toLocaleDateString('pt-BR') : '';
  };

  const handlePresetClick = (preset) => {
    if (preset.id === 'personalizado') {
      // Não faz nada, apenas mantém o dropdown aberto
      return;
    }
    
    const dates = preset.getDates();
    onChange({ presetId: preset.id, ...dates });
    setIsOpen(false);
  };

  const validateDates = (start, end) => {
    const startDate = parseISODateLocal(start);
    const endDate = parseISODateLocal(end);
    if (!startDate || !endDate) return true;
    return startDate <= endDate;
  };

  const handleDateChange = (field, newValue) => {
    const newDates = { 
      ...value, 
      [field]: newValue,
      presetId: 'personalizado' // Sempre define como personalizado quando edita datas
    };
    
    // Se ambas as datas estão preenchidas, valida
    if (newDates.start && newDates.end) {
      if (validateDates(newDates.start, newDates.end)) {
        onChange(newDates);
      }
    } else {
      // Se apenas uma data está preenchida, atualiza sem validar
      onChange(newDates);
    }
  };

  return (
    <div className="relative max-w-full" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className="flex min-h-[44px] items-center gap-2 px-3 py-2 border border-[color:var(--border)] rounded-md text-sm font-medium text-[color:var(--text-primary)] bg-[color:var(--surface)] hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)] disabled:opacity-50 transition-colors max-w-full"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Selecionar período de data"
      >
        <Calendar className="h-4 w-4 flex-shrink-0" />
        <span className="truncate max-w-[120px] sm:max-w-32">
          {value.presetId === 'personalizado' 
            ? `${formatDate(value.start)} - ${formatDate(value.end)}`
            : presets.find(p => p.id === value.presetId)?.label || 'Mês Atual'
          }
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Overlay para fechar o dropdown - abaixo dos modais (z-40) */}
          <div 
            className="fixed inset-0 z-[80]" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown - z-50 igual aos outros componentes e modais */}
          <div className="absolute right-0 mt-2 w-56 max-w-[calc(100vw-2rem)] bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg shadow-xl z-[95]">
            <div className="py-2" role="listbox" aria-label="Períodos de data">
            {presets.map((preset) => (
              <button
                key={preset.id}
                onClick={() => handlePresetClick(preset)}
                className={`w-full min-h-[44px] text-left px-4 py-2.5 text-sm hover:bg-[color:var(--surface-muted)] transition-colors ${
                  value.presetId === preset.id 
                    ? 'bg-[color:var(--surface-muted)] text-[color:var(--accent)] font-medium' 
                    : 'text-[color:var(--text-primary)]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{preset.label}</span>
                  {value.presetId === preset.id && (
                    <div className="w-2 h-2 bg-[color:var(--accent)] rounded-full"></div>
                  )}
                </div>
              </button>
            ))}
          </div>
          
          <div className="border-t border-[color:var(--border)] p-4 bg-[color:var(--surface-muted)]">
            <div className="text-xs text-[color:var(--text-muted)] mb-3">Consulta personalizada</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <input
                  type="date"
                  value={value.start || ''}
                  onChange={(e) => handleDateChange('start', e.target.value)}
                  className="w-full min-h-[44px] rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                  placeholder="Início"
                  aria-label="Data de início"
                />
              </div>
              <div>
                <input
                  type="date"
                  value={value.end || ''}
                  onChange={(e) => handleDateChange('end', e.target.value)}
                  className="w-full min-h-[44px] rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                  placeholder="Fim"
                  aria-label="Data de fim"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => {
                  if (value.start && value.end) {
                    setIsOpen(false);
                  }
                }}
                disabled={!value.start || !value.end}
                className="flex-1 min-h-[44px] px-3 py-2 text-sm bg-[color:var(--accent)] text-white rounded-md hover:brightness-110 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Aplicar
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="min-h-[44px] px-3 py-2 text-sm border border-[color:var(--border)] text-[color:var(--text-primary)] rounded-md hover:bg-[color:var(--surface-muted)] transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FiltroData;
